import { createContext, useReducer, useContext } from "react";
import { Step } from "../../interfaces";

export interface State {
  showSteps: boolean;
  active: number;
  flow: Step[];
  isNextBtnVisible: boolean;
}

interface IdentityProviderProps {
  children: React.ReactNode;
  initialState: State;
}

type Action =
  | { type: "showSteps"; payload: { showSteps: boolean } }
  | { type: "changeActive"; payload: { active: number } }
  | { type: "changeFlow"; payload: { flow: Step[] } }
  | { type: "changeNextBtnVisibility"; payload: { isNextBtnVisible: boolean } };

type Dispatch = (action: Action) => void;

const StepByStepStateContext = createContext<State | undefined>(undefined);
const StepByStepDispatchContext = createContext<Dispatch | undefined>(
  undefined
);

const stepByStepReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "showSteps":
    case "changeFlow":
    case "changeActive":
    case "changeNextBtnVisibility":
      return {
        ...state,
        ...action.payload
      };
    default: {
      throw new Error();
    }
  }
};

const StepByStepProvider = ({
  children,
  initialState
}: IdentityProviderProps) => {
  const [state, dispatch] = useReducer(stepByStepReducer, initialState);

  return (
    <StepByStepStateContext.Provider value={state}>
      <StepByStepDispatchContext.Provider value={dispatch}>
        {children}
      </StepByStepDispatchContext.Provider>
    </StepByStepStateContext.Provider>
  );
};

StepByStepProvider.defaultProps = {
  initialState: {
    active: 0,
    showSteps: false,
    flow: [] as Step[],
    isNextBtnVisible: true
  }
};

const useStepByStepState = () => {
  const context = useContext(StepByStepStateContext);

  if (context === undefined) {
    throw new Error("useStepByStepState must be withing StepByStepProvider");
  }

  return context;
};

const useStepByStepDispatch = () => {
  const context = useContext(StepByStepDispatchContext);

  if (context === undefined) {
    throw new Error("useStepByStepDispatch must be withing StepByStepProvider");
  }

  return context;
};

export { StepByStepProvider, useStepByStepState, useStepByStepDispatch };
